import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { showErrorDialog } from 'store/errorDialog/actions';
import { selectDeeplinkValid } from 'store/programs/selectors';
import {
    MixedItem,
    PlaybackTypes,
    RBProgram,
} from 'store/railsBuilder/reducer.types';
import { ReduxStore } from 'store/types';

import { ERROR_CODE_A013 } from 'utils/error/errorCodes';
import getChannelId from 'utils/getChannelId';
import getDeeplinkItem from 'utils/getDeeplinkItem';
import useTuneToLiveProgram from 'utils/hooks/useTuneToLiveProgram';
import openDeeplink from 'utils/openDeeplink';

import { isLive } from 'screens/Epg/utils/program';

import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

type Props = {
    item: MixedItem | RBProgram | undefined;
    ableToPlayCatchup: boolean;
    playbackType: PlaybackTypes | undefined;
    index: number | undefined;
} & OnUavAnalyticsProps &
    StandardUavProps;

export default function useDeeplinkItem({
    item,
    ableToPlayCatchup = false,
    onDeepLinkTriggered,
    onThirdPartyAppStart,
    onRailsSelection,
    railId,
    templateId,
    playbackType,
    index = 0,
}: Props) {
    const deepLinkItem = getDeeplinkItem(item);

    const dispatch = useDispatch();
    const tuneToLiveProgram = useTuneToLiveProgram();

    const isValidDeeplinkItem = useSelector((state: ReduxStore) =>
        selectDeeplinkValid(state, deepLinkItem),
    );

    const deeplinkCanBeOpened = !!(deepLinkItem && isValidDeeplinkItem);

    const deeplinkErrorNeedToBeShown = !!(deepLinkItem && !isValidDeeplinkItem);

    const showDeeplinkOpeningError = useCallback(() => {
        dispatch(
            showErrorDialog({
                title: LANGUAGE_KEYS.ERR_A013_INVALID_DEEPLINK_TITLE,
                description:
                    LANGUAGE_KEYS.ERR_A013_INVALID_DEEPLINK_DESCRIPTION,
                code: ERROR_CODE_A013,
            }),
        );
    }, [dispatch]);

    const onPressPossibleDeeplinkItem = useCallback(
        (onPress: () => void, tuneToLive: boolean = false) => {
            if (ableToPlayCatchup) {
                onPress();
            } else if (item && deeplinkCanBeOpened) {
                openDeeplink(
                    item,
                    onDeepLinkTriggered,
                    onThirdPartyAppStart,
                    playbackType,
                ).catch(() => {
                    showDeeplinkOpeningError();
                });
            } else if (deeplinkErrorNeedToBeShown) {
                showDeeplinkOpeningError();
            } else if (tuneToLive && isLive(item) && getChannelId(item)) {
                tuneToLiveProgram(
                    getChannelId(item),
                    railId,
                    templateId,
                    onRailsSelection,
                    index,
                );
            } else {
                onPress();
            }
        },
        [
            item,
            deeplinkCanBeOpened,
            deeplinkErrorNeedToBeShown,
            onDeepLinkTriggered,
            onThirdPartyAppStart,
            showDeeplinkOpeningError,
            ableToPlayCatchup,
            tuneToLiveProgram,
            templateId,
            railId,
            onRailsSelection,
            playbackType,
            index,
        ],
    );

    return {
        deepLinkItem,
        deeplinkCanBeOpened,
        deeplinkErrorNeedToBeShown,
        isValidDeeplinkItem,
        onPressPossibleDeeplinkItem,
        isDeeplinkItem: !!deepLinkItem,
    };
}
